import { Particle } from '../types/algorithm';
import { calculateParticleFitness } from '../utils/fitnessCalculator';
import {
  calculateRWP,
  rouletteWheelSelection,
  singlePointCrossover,
} from './geneticAlgorithm';
import {
  initializeSwarm,
  findGlobalBest,
  updateParticle,
} from './pso';

/**
 * Generalized Adaptive PSO (GAPSO) implementation from scratch
 * Hybrid of PSO and GA - uses GA operators (crossover) in PSO framework
 */

/**
 * GAPSO evolution for one generation
 */
function evolveGAPSO(
  swarm: Particle[],
  globalBest: Particle,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  generation: number,
  maxGenerations: number,
  crossoverRate: number
): void {
  // Linearly decreasing inertia weight
  const wMax = 0.9;
  const wMin = 0.4;
  const w = wMax - ((wMax - wMin) * generation) / maxGenerations;

  const c1 = 2.0;
  const c2 = 2.0;

  // PSO update for all particles
  for (const particle of swarm) {
    updateParticle(particle, globalBest, taskCount, serviceCount, w, c1, c2);
    calculateParticleFitness(particle, matrix, taskCount, serviceCount);
  }

  // GA crossover operations (hybrid enhancement)
  const crossoverCount = Math.floor((swarm.length * crossoverRate) / 100);
  
  // Create temporary chromosome-like structures for RWP calculation
  const tempChromosomes = swarm.map(p => ({
    x: p.position,
    fitness: p.fitness,
    rwp: 0,
  }));
  calculateRWP(tempChromosomes);

  for (let i = 0; i < crossoverCount; i++) {
    // Use roulette wheel selection on temp chromosomes (which have RWP calculated)
    const parent1ChromIdx = rouletteWheelSelection(tempChromosomes);
    const parent2ChromIdx = rouletteWheelSelection(tempChromosomes);
    const parent1Idx = parent1ChromIdx;
    const parent2Idx = parent2ChromIdx;

    // Create temporary chromosomes for crossover
    const parent1 = { x: [...swarm[parent1Idx].position], fitness: swarm[parent1Idx].fitness, rwp: 0 };
    const parent2 = { x: [...swarm[parent2Idx].position], fitness: swarm[parent2Idx].fitness, rwp: 0 };

    const [child] = singlePointCrossover(parent1, parent2, taskCount);

    // Create new particle from child
    const newParticle: Particle = {
      position: child.x,
      velocity: swarm[parent1Idx].velocity,
      personalBest: [...swarm[parent1Idx].personalBest],
      fitness: 0,
      personalBestFitness: swarm[parent1Idx].personalBestFitness,
      rwp: 0,
    };

    calculateParticleFitness(newParticle, matrix, taskCount, serviceCount);

    // Replace parent if child is better
    if (newParticle.fitness > swarm[parent1Idx].fitness) {
      swarm[parent1Idx] = newParticle;
    }
  }
}

/**
 * Runs GAPSO for specified generations
 */
export async function runGAPSO(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    crossoverRate: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void
): Promise<Particle> {
  // Initialize swarm
  let swarm = initializeSwarm(parameters.populationSize, taskCount, serviceCount);

  // Evaluate initial swarm
  for (const particle of swarm) {
    calculateParticleFitness(particle, matrix, taskCount, serviceCount);
  }

  let globalBest = findGlobalBest(swarm);

  // Evolution loop
  for (let gen = 0; gen < parameters.generations; gen++) {
    evolveGAPSO(
      swarm,
      globalBest,
      taskCount,
      serviceCount,
      matrix,
      gen,
      parameters.generations,
      parameters.crossoverRate
    );
    globalBest = findGlobalBest(swarm);

    // Report progress
    if (onGenerationComplete) {
      onGenerationComplete(gen + 1, globalBest.fitness);
    }

    // Yield control to allow UI updates
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  return globalBest;
}

